import React, { useState, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { EventDatetimeContext } from 'components/EventDatetime';
import Footer from 'components/Footer';
import BackgroundImage from 'assets/landing/background.png';
import GAAIcon from 'assets/landing/gaa_icon.png';
import HKSTPIcon from 'assets/landing/hkstp_icon.png';
import iCielGothamFontTTF from 'assets/landing/iCiel-Gotham.ttf';
import iCielGothamFontEOT from 'assets/landing/iCiel-Gotham.eot';
import DINCondensedFontWOFF from 'assets/landing/DIN-Condensed-Bold.woff';
import DINCondensedFontEOT from 'assets/landing/DIN-Condensed-Bold.eot';
import HomepageBaukastenFontTTF from 'assets/landing/HomepageBaukasten-Book.ttf';
import Countdown from '../../Timer';
import RegisterModal from '../../RegisterModal';
import ResetEmailModal from './ResetEmailModal';
import TopBar from '../../TopBar';

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  55% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const enlength = keyframes`
  0% {
    position: absolute;
    border: solid 3px #FF6D62;
    width: max(200vh, 200vw);
    height: max(200vh, 200vw);
    border-radius: 50%;
    left: max(-100vh, -100vw);
    top: max(-100vh, -100vw);
  }

  60% {
    top: 0;
    left: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  65% {
    transform: translateY(0);
    width: 12px;
    height: 12px;
  }

  72% {
    transform: translateY(-24px);
    width: 12px;
    height: 12px;
  }

  77% {
    transform: translateY(0);
    width: 12px;
    height: 12px;
  }

  78% {
    width: 1px;
    height: 1px;
    border-radius: 50%;
  }
`;

const fadeInLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-50px);
  }

  80% {
    opacity: 0;
    transform: translateX(-50px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInBottom = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px);
  }

  80% {
    opacity: 0;
    transform: translateY(30px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LandingPageBackgroundContainer = styled.div`
  @font-face {
    font-family: iCiel-Gotham;
    src:
      url(${iCielGothamFontTTF}),
      url(${iCielGothamFontEOT}?#iefix) format('embedded-opentype');
  }

  @font-face {
    font-family: DIN-Condensed;
    src:
      url(${DINCondensedFontWOFF}) format('woff'),
      url(${DINCondensedFontEOT}?#iefix) format('embedded-opentype');
  }

  @font-face {
    font-family: HomepageBaukasten;
    src: url(${HomepageBaukastenFontTTF});
  }

  background-image: url(${BackgroundImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  padding-left: 2rem;
  padding-right: 2rem;
`;

const LandingPageContainer = styled.div`
  max-width: 610px;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: auto;
  transition: all 1s;
`;

const LandingPageWrapper = styled.div`
  padding-top: 50px;
  display: flex;
  direction: column;
  justify-content: center;
  flex-direction: column;
`;

const EventName = styled.div`
  font-family: DIN-Condensed, sans-serif;
  font-weight: bold;
  font-size: 20px;
  display: flex;
  align-items: center;
  color: #FFF;
  transition: all 1s;
  animation: ${fadeIn} 2.5s ease-in;
  animation-delay: ${({ delay }) => `${delay}s`};

  @media only screen and (min-width: 960px) {
    font-size: 72px;
    line-height: 72px;
  }

  @media only screen and (max-width: 960px) {
    font-size: 42px;
    line-height: 42px;
  }
`;

const DividerContainer = styled.div`
  width: 100%;
  position: relative;
  margin-top: 20px;
`;

const Divider = styled.div`
  position: absolute;
  border: solid 1px #FF6D62;
  background-color: #FF6D62;
  width: 100%;
  top: 50%;
  animation: ${enlength} 2.5s ease-in-out;
`;

const LiveEverywhereContainer = styled.div`
  flex: 1 1 auto;
  font-family: iCiel-Gotham;
  width: 100%;
  color: #FF6D62;
  font-weight: 300;
  letter-spacing: 1px;
  align-self: center;
  transition: all 1s;
  animation: ${fadeInLeft} 3s ease-in;
  display: flex;
  align-items: center;

  @media only screen and (min-width: 960px) {
    font-size: 27px;
  }

  @media
    (min-width: 960px) and (-ms-high-contrast: none),
    (min-width: 960px) and (-ms-high-contrast: active) {
    height: 27px;
  }

  @media only screen and (max-width: 960px) {
    font-size: 12px;
  }

  @media
    (max-width: 960px) and (-ms-high-contrast: none),
    (max-width: 960px) and (-ms-high-contrast: active) {
    height: 12px;
  }
`;

const StyledPlayCircleOutlineIcon = withStyles(() => ({
  root: {
    color: '#FF6D62',
    fontSize: '24px',
    marginRight: '8px',
    '@media (max-width: 960px)': {
      fontSize: '14px',
      marginRight: '4.5px',
    },
  },
}))(PlayCircleOutlineIcon);

const LiveEverywhereText = styled.div`
  height: 100%;
`;

const EventCountDownContainer = styled.div`
  width: 100%;
  animation: ${fadeInBottom} 3s ease-in;
`;

const EventCountDown = styled.div`
  width: 100%;
  padding: 1rem 0;
  text-align: center;
  color: #FFF;
  font-size: 30px;
  font-weight: 600;
  z-index: 1;

  @media only screen and (min-width: 900px) {
    padding: 2rem 0;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  padding: 40px 0;
  text-align: center;
  transition: all 1s;
  margin-top: 15px;
  animation: ${fadeInBottom} 3s ease-in;
`;

const RegisterButton = styled.button`
  width: 100%;
  height: 48px;
  margin: 8px auto;
  background: #282B40;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  font-size: 18px;
  line-height: 40px;
  text-align: center;
  letter-spacing: -0.408px;
  color: #FFF;
  cursor: pointer;
  outline: none;
`;

const LoginButton = styled.button`
  width: 100%;
  height: 48px;
  margin: 8px auto;
  background: #FFF;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  font-size: 18px;
  line-height: 40px;
  text-align: center;
  letter-spacing: -0.408px;
  color: #282B40;
  cursor: pointer;
  outline: none;
`;

const EventEndText = styled.div`
  margin-top: 50px;
  font-weight: 500;
  font-size: 60px;
  line-height: 80px;
  text-align: center;
  letter-spacing: -0.408px;
  color: #FFF;
  animation: ${fadeInBottom} 3s ease-in;
`;

const EventPeriodContainer = styled.div`
  flex: 0 1 auto;
  font-family: HomepageBaukasten, Lato;
  background-color: #FF6D62;
  white-space: nowrap;
  text-align: center;
  color: #FFF;
  font-size: 20px;
  font-weight: 700;
  transition: all 1s;
  animation: ${fadeInLeft} 3s ease-in;
  text-transform: uppercase;
  display: flex;
  justify-content: center;

  @media (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: 0 0 auto;
  }

  @media only screen and (min-width: 960px) {
    padding: 17px 35px;
    width: 230px;
    height: 66px;
    font-size: 32px;
    line-height: 32px;
  }

  @media only screen and (max-width: 960px) {
    padding: 13px 20px;
    width: 140px;
    height: 40px;
    font-size: 14px;
    line-height: 14px;
  }
`;

const FooterContainer = styled.div`
  transition: all 1s;
  animation: ${fadeInBottom} 3s ease-in;
`;

const FlexBox = styled.div`
  display: flex;
`;

const PageUpDownSplitter = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;

  & > :first-child {
    flex: 0 1 auto;
  }

  & > :last-child {
    flex: 1 1 auto;
  }
`;

const StyledImg = styled.img`
  width: 60px;
  height: auto;
  object-fit: contain;
  transition: all 1s;
  animation: ${fadeIn} 2.5s ease-in;
  animation-delay: ${({ delay }) => `${delay}s`};

  @media screen and (min-width: 500px) {
    width: 77px;
  }

  @media screen and (min-width: 900px) {
    width: 120px;
  }
`;

const TitleWithIconContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: start;
  position: relative;
  top: -5%;
  right: -5%;
`;

const LandingPage = () => {
  const [isRegisterModalOpened, setIsRegisterModalOpened] = useState(false);
  const [isResendEmailModalOpened, setIsResendEmailModalOpened] = useState(false);
  const {
    hasEventEnded,
    startDatetime,
    endDatetime,
    hasRegisterStarted,
    hasRegisterEnded,
    hasStageProgrammeRegisterEnded,
  } = useContext(EventDatetimeContext);

  return (
    <PageUpDownSplitter>
      <TopBar />
      <LandingPageBackgroundContainer>
        <LandingPageContainer>
          <LandingPageWrapper>
            <TitleWithIconContainer>
              <div>
                <EventName delay={0.2}>GLOBAL</EventName>
                <EventName delay={0.5}>MATCHING</EventName>
                <EventName delay={0.8}>2020</EventName>
              </div>

              <IconContainer>
                <StyledImg delay={0.2} src={GAAIcon} />
                <StyledImg delay={0.5} src={HKSTPIcon} />
              </IconContainer>
            </TitleWithIconContainer>

            <DividerContainer>
              <Divider />
            </DividerContainer>

            <FlexBox>
              <LiveEverywhereContainer>
                <StyledPlayCircleOutlineIcon />
                <LiveEverywhereText>
                  Live Everywhere
                </LiveEverywhereText>
              </LiveEverywhereContainer>
              <EventPeriodContainer>
                {`${startDatetime.format('DD')}-${endDatetime.format('DD MMM')}`}
              </EventPeriodContainer>
            </FlexBox>

            {!hasRegisterStarted && (
              <EventCountDownContainer>
                <EventCountDown />
                <Countdown />
              </EventCountDownContainer>
            )}

            <EventEndText>
              Event Finished
              <br />
              See You Next Time
            </EventEndText>

            <RegisterModal
              open={isRegisterModalOpened}
              handleClose={() => { setIsRegisterModalOpened(false); }}
            />
            <ResetEmailModal
              open={isResendEmailModalOpened}
              handleClose={() => { setIsResendEmailModalOpened(false); }}
              onRegisterNowBtnClick={() => {
                setIsResendEmailModalOpened(false);
                setIsRegisterModalOpened(true);
              }}
            />
          </LandingPageWrapper>
          <FooterContainer>
            <Footer />
          </FooterContainer>
        </LandingPageContainer>
      </LandingPageBackgroundContainer>
    </PageUpDownSplitter>
  );
};

export default LandingPage;
